import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const TEMPORARY_PERMISSION_SEARCH_RESOURCE = "temporary-permission" as const;
export const TEMPORARY_PERMISSION_SEARCH_OPERATION = "search" as const;
export const TEMPORARY_PERMISSION_SEARCH_API_PATH =
  `/v1/${TEMPORARY_PERMISSION_SEARCH_RESOURCE}/${TEMPORARY_PERMISSION_SEARCH_OPERATION}` as const;

export type TemporaryPermissionSearchParameters =
  paths[typeof TEMPORARY_PERMISSION_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type TemporaryPermissionSearchBody =
  paths[typeof TEMPORARY_PERMISSION_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type TemporaryPermissionSearchResponse =
  paths[typeof TEMPORARY_PERMISSION_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useTemporaryPermissionSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<TemporaryPermissionSearchParameters, TemporaryPermissionSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    TemporaryPermissionSearchResponse,
    Error,
    TemporaryPermissionSearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<TemporaryPermissionSearchParameters, TemporaryPermissionSearchBody>>(
      options,
    );

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      TEMPORARY_PERMISSION_SEARCH_RESOURCE,
      TEMPORARY_PERMISSION_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<TemporaryPermissionSearchResponse>(
        TEMPORARY_PERMISSION_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
